// Linguagem visual da secao Marketing. Convencao: nivel 1 = regiao da UI,
// nivel 2 = papel do elemento.
//
// REGRA DE OURO: superficie usa classe semantica do shadcn (bg-card,
// border-border, text-foreground, text-muted-foreground, bg-muted) e NUNCA
// `bg-white dark:bg-X` — o shim legado de src/index.css vem depois de
// @tailwind utilities e vence `dark:` no empate de especificidade (ambos
// (0,2,0), ele por ultimo). Cor de marca = brand-*. Cor de dominio
// (status/plataforma) = os pares TONE de src/design/tokens.
//
// Uma pagina compoe assim:
//   export const X_DESIGN = { ...MKT, secaoPropria: {...} };
// Pode ADICIONAR chaves e mapas de dominio. NAO pode redefinir uma chave
// compartilhada com valor diferente — se um token daqui nao serve, ele esta
// errado e muda aqui, nao na pagina.
import { TONE, TONE_DOT, TONE_SOFT, CHART } from 'src/design/tokens';

export const MKT = {
    page: {
        container: 'px-4 md:px-8 lg:px-12 pb-8 pt-6 max-w-[1600px] mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500',
        stack: 'space-y-6',
        // Par para paginas com MktCompactHeader (header de barra sticky, no
        // padrao de Campanhas.js): o header fica fora do padding/max-w pra
        // ocupar a largura toda; `content` embrulha o resto com o mesmo
        // padding/max-w que `container` ja tinha.
        containerCompact: 'h-full overflow-y-auto',
        content: 'max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 pb-8 pt-6 animate-in fade-in slide-in-from-bottom-4 duration-500',
    },
    // Header de barra sticky compacta (PageContainerHeader), no padrao de
    // Campanhas.js — usado por MktCompactHeader. Distinto de `header` acima
    // (o header "hero" grande usado por MktPageHeader em paginas sem filtros
    // inline).
    compactHeader: {
        iconBox: 'p-2 bg-violet-500/10 rounded-lg',
        // Logo de terceiro (logos:facebook, logos:whatsapp-icon...): superficie
        // neutra, pra cor da marca ser a unica cor do bloco. Ver header.iconBoxBrand.
        iconBoxBrand: 'p-2 bg-muted border border-border rounded-lg',
        icon: 'text-violet-600 dark:text-violet-400',
        title: 'text-sm font-semibold text-foreground',
        backBtn: 'p-2 rounded-lg text-muted-foreground hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-500/10 transition-colors flex-shrink-0',
        filterLabel: 'text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 block pl-1',
        filterControl: 'h-9 text-xs rounded-lg',
    },
    header: {
        wrapper: 'flex items-start justify-between gap-4 mb-6 flex-wrap',
        left: 'flex items-center gap-3 min-w-0',
        backBtn: 'w-9 h-9 rounded-xl border border-border bg-card flex items-center justify-center hover:bg-accent transition-colors text-muted-foreground hover:text-foreground flex-shrink-0',
        iconBox: 'w-11 h-11 rounded-2xl flex items-center justify-center bg-violet-100 dark:bg-violet-500/15 flex-shrink-0',
        // Variante pra logo de terceiro: `iconBox` tinge o icone de violeta
        // (`icon` abaixo), o que descaracteriza uma marca e ainda joga o azul
        // do Facebook contra um fundo violeta. Marca sai na cor dela, sobre
        // superficie neutra.
        iconBoxBrand: 'w-11 h-11 rounded-2xl flex items-center justify-center bg-muted border border-border flex-shrink-0',
        icon: 'text-violet-600 dark:text-violet-400',
        title: 'text-xl md:text-2xl font-bold text-foreground truncate',
        subtitle: 'text-sm text-muted-foreground',
        actions: 'flex items-center gap-2 flex-wrap justify-end',
        badgeRow: 'flex items-center gap-2 flex-wrap flex-shrink-0',
        metaRow: 'flex flex-wrap items-center gap-x-6 gap-y-3 mt-4 pt-4 border-t border-border',
        metaIconBox: 'w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0',
        metaLabel: 'text-xs font-semibold text-muted-foreground uppercase tracking-wider',
        metaValue: 'text-sm font-semibold text-foreground',
    },
    surface: {
        card: 'bg-card text-card-foreground rounded-xl border border-border',
        cardPad: 'p-5',
        cardTitle: 'text-sm font-semibold text-foreground flex items-center gap-2 mb-4',
        cardSub: 'text-xs text-muted-foreground -mt-3 mb-4',
        sectionIconBox: 'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
    },
    tabs: {
        container: 'flex items-center gap-1 mb-6 bg-muted rounded-xl p-1 w-fit',
        tab: 'px-4 py-2 text-sm font-medium rounded-lg cursor-pointer transition-all text-muted-foreground hover:text-foreground',
        tabActive: 'px-4 py-2 text-sm font-medium rounded-lg cursor-pointer transition-all bg-card text-violet-700 dark:text-violet-400 shadow-sm',
    },
    stats: {
        grid: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-6',
        gridWide: 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-6',
        card: 'bg-card rounded-xl border border-border p-4 flex items-center gap-3 transition-shadow duration-300 hover:shadow-lg',
        cardPlain: 'bg-card rounded-xl border border-border p-4 transition-shadow duration-300 hover:shadow-lg',
        iconBox: 'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
        value: 'text-xl font-bold text-foreground tabular-nums',
        label: 'text-xs text-muted-foreground',
        growth: {
            positive: 'text-xs font-medium text-green-600 dark:text-green-400 flex items-center gap-0.5',
            negative: 'text-xs font-medium text-red-600 dark:text-red-400 flex items-center gap-0.5',
            neutral: 'text-xs font-medium text-muted-foreground flex items-center gap-0.5',
        },
    },
    // Usado por PeriodFilter, que agora vive dentro do MktCompactHeader (sem
    // margem propria — quem embrulha decide o espacamento).
    filters: {
        bar: 'flex flex-wrap items-end gap-3',
        group: 'flex flex-col gap-1',
        label: 'text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 block pl-1',
        control: 'h-9 text-xs rounded-lg',
    },
    table: {
        container: 'bg-card rounded-xl border border-border overflow-hidden',
        toolbar: 'px-5 py-3 border-b border-border flex items-center justify-between gap-2',
        toolbarTitle: 'text-sm font-semibold text-foreground',
        head: 'border-b border-border bg-muted/50 px-5 py-3 flex items-center text-xs font-semibold text-muted-foreground uppercase tracking-wider',
        row: 'group px-5 py-3 flex items-center border-b last:border-b-0 border-border/60 hover:bg-accent/50 transition-colors',
        cell: 'text-sm text-foreground',
        cellMuted: 'text-xs text-muted-foreground',
        numeric: 'text-sm font-medium text-foreground tabular-nums text-right',
        actions: 'flex items-center gap-1 flex-shrink-0',
        actionBtn: 'h-8 w-8 p-0 rounded-lg text-muted-foreground hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-500/10 transition-colors',
        actionBtnDanger: 'h-8 w-8 p-0 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors',
        pagination: 'flex flex-wrap justify-between items-center gap-3 p-4 border-t border-border bg-muted/30',
        pageInfo: 'text-sm text-muted-foreground tabular-nums',
        thumb: 'w-10 h-10 rounded-lg object-cover bg-muted flex-shrink-0',
        thumbPlaceholder: 'w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 text-muted-foreground',
    },
    chip: {
        base: 'text-xs font-medium inline-flex items-center gap-1.5 border px-2.5 py-1 rounded-full whitespace-nowrap',
        sm: 'text-xs font-medium inline-flex items-center gap-1 border px-2 py-0.5 rounded-full whitespace-nowrap',
        dot: 'w-1.5 h-1.5 rounded-full flex-shrink-0',
    },
    emptyState: {
        container: 'flex flex-col items-center justify-center py-16 px-6 text-center',
        iconBox: 'w-16 h-16 rounded-2xl flex items-center justify-center bg-violet-50 dark:bg-violet-500/10 mb-4',
        icon: 'text-violet-600 dark:text-violet-400',
        title: 'text-lg font-semibold text-foreground mb-1',
        subtitle: 'text-sm text-muted-foreground mb-4 max-w-sm',
        action: 'text-sm font-medium text-violet-600 dark:text-violet-400 hover:underline cursor-pointer',
    },
    loading: {
        box: 'flex flex-col items-center justify-center gap-3 py-20',
        spinner: 'w-8 h-8 border-2 border-violet-200 dark:border-violet-500/30 border-t-violet-600 dark:border-t-violet-400 rounded-full animate-spin',
        text: 'text-sm text-muted-foreground',
        skeleton: 'animate-pulse rounded-xl bg-muted',
    },
    chart: {
        grid2: 'grid grid-cols-1 lg:grid-cols-2 gap-4',
        grid3: 'grid grid-cols-1 lg:grid-cols-3 gap-4',
        box: 'h-[280px] w-full',
        boxTall: 'h-[360px] w-full',
    },
    tone: TONE,
    toneDot: TONE_DOT,
    toneSoft: TONE_SOFT,
    chartDefaults: CHART,
};
